import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';
import { setAllPersonProfilePropertiesAsPersonPropertiesForFlags } from 'posthog-js/lib/src/customizations/index.js';

/**
 * @description Initializes PostHog analytics with the provided brand and source information. This function checks for the necessary environment variables and sets up PostHog with appropriate configurations, including feature flag handling and user property registration.
 * @param {Object} params - The parameters for initializing PostHog.
 * @param {string} params.brand - The brand associated with the analytics data.
 * @param {string} params.source - The source of the analytics data.
 * @param {string} [params.host] - Optional host information for additional context in analytics.
 * @param {string} [params.key] - Optional API key for PostHog initialization.
 * @returns {Promise<void>} A promise that resolves when PostHog is successfully initialized, or rejects if there is an error during initialization.
 * @throws {Error} Throws an error if the PostHog API key or host is not defined in the environment variables.
 */
export const initPostHog = ({
  brand,
  source,
  host,
  key,
}: {
  brand: string;
  source: string;
  host?: string;
  key?: string;
}): Promise<void> => {
  log.debug({ message: 'Initializing PostHog', data: { brand, source } });

  return new Promise<void>((resolve, reject) => {
    if (!key || !host) {
      return reject(new Error('PostHog API key or host is not defined in environment variables'));
    }

    posthog.init(key, {
      api_host: host,
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      persistence: 'localStorage',
      defaults: '2026-01-30',
      loaded: (ph) => {
        // On feature flags gets invoked even if the flags request is successful or unsuccessful, so we can use it to ensure that PostHog has finished its initialization process
        // Complete in 3 seconds if the flags request fails to prevent hanging the app indefinitely
        setAllPersonProfilePropertiesAsPersonPropertiesForFlags(ph);
        ph.onFeatureFlags(() => {
          resolve();
        });

        ph.register({
          brand,
          source,
        });
      },
    });
  });
};
